import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';
import './TeacherAssignments.css';

const TeacherAssignments = ({ teacherId }) => {
  const [assignments, setAssignments] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (teacherId) {
      fetchAssignments();
    } else {
      setError('Teacher ID is required');
      setLoading(false);
    }
  }, [teacherId]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      console.log('Fetching assignments for teacherId:', teacherId);

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/assignments/teacher`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'teacher-id': teacherId
        }
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response:', errorText);
        throw new Error(`Failed to fetch assignments: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Fetched assignments:', data);
      setAssignments(data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubmissions = (assignment) => {
    setSelectedAssignment(assignment);
  };

  const handleCreateAssignment = () => {
    setShowCreateModal(true);
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'teacher-id': teacherId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete assignment');
      }

      // Refresh assignments list
      fetchAssignments();
      alert('Assignment deleted successfully!');
    } catch (error) {
      console.error('Error deleting assignment:', error);
      alert('Failed to delete assignment: ' + error.message);
    }
  };

  if (loading) {
    return <div className="loading">Loading assignments...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="teacher-assignments">
      <div className="assignments-header">
        <h2>My Assignments</h2>
        <button
          className="create-assignment-btn"
          onClick={handleCreateAssignment}
        >
          Create New Assignment
        </button>
      </div>

      <div className="assignments-list">
        {assignments.length === 0 ? (
          <div className="no-assignments">
            <p>You haven't created any assignments yet.</p>
            <button
              className="create-first-assignment-btn"
              onClick={handleCreateAssignment}
            >
              Create Your First Assignment
            </button>
          </div>
        ) : (
          assignments.map(assignment => (
            <div key={assignment.id} className="assignment-card">
              <div className="assignment-info">
                <h3>{assignment.title}</h3>
                <p className="assignment-description">{assignment.description}</p>
                <div className="assignment-details">
                  <span className="class-info">Class: {assignment.className}</span>
                  <span className="subject-info">Subject: {assignment.subjectName}</span>
                  <span className="questions-count">
                    {assignment.totalQuestions} questions, {assignment.totalPoints} points
                  </span>
                  {assignment.dueDate && (
                    <span className="due-date">
                      Due: {new Date(assignment.dueDate).toLocaleDateString()}
                    </span>
                  )}
                  <span className={`status ${assignment.status}`}>
                    {assignment.status}
                  </span>
                </div>
                <div className="assignment-stats">
                  <span>Assigned to: {assignment.assignedStudents.length} students</span>
                </div>
              </div>
              <div className="assignment-actions">
                <button
                  className="view-submissions-btn"
                  onClick={() => handleViewSubmissions(assignment)}
                >
                  View Submissions
                </button>
                <button
                  className="delete-assignment-btn"
                  onClick={() => handleDeleteAssignment(assignment.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <CreateAssignmentModal
          teacherId={teacherId}
          onClose={() => setShowCreateModal(false)}
          onAssignmentCreated={fetchAssignments}
        />
      )}

      {selectedAssignment && (
        <AssignmentSubmissionsModal
          assignment={selectedAssignment}
          teacherId={teacherId}
          onClose={() => setSelectedAssignment(null)}
        />
      )}
    </div>
  );
};

// Create Assignment Modal Component
const CreateAssignmentModal = ({ teacherId, onClose, onAssignmentCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    className: '',
    subjectName: '',
    selectedQuestions: [],
    assignedStudents: [],
    dueDate: '',
    instructions: ''
  });
  const [availableExams, setAvailableExams] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Basic info, 2: Select questions, 3: Assign students

  useEffect(() => {
    if (step === 2) {
      fetchAvailableExams();
    } else if (step === 3) {
      fetchAvailableStudents();
    }
  }, [step]);

  const fetchAvailableExams = async () => {
    try {
      // Get midterm exams
      const midtermResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/midterm`);
      const midtermExams = await midtermResponse.json();

      // Get final exams
      const finalResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/final`);
      const finalExams = await finalResponse.json();

      setAvailableExams([...midtermExams, ...finalExams]);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchAvailableStudents = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.STUDENTS}`);
      const students = await response.json();

      // Filter students by selected class
      const classStudents = students.filter(student =>
        student.className === formData.className
      );
      setAvailableStudents(classStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleQuestionSelect = (questionId, checked) => {
    setFormData(prev => ({
      ...prev,
      selectedQuestions: checked
        ? [...prev.selectedQuestions, questionId]
        : prev.selectedQuestions.filter(id => id !== questionId)
    }));
  };

  const handleStudentSelect = (studentId, checked) => {
    setFormData(prev => ({
      ...prev,
      assignedStudents: checked
        ? [...prev.assignedStudents, studentId]
        : prev.assignedStudents.filter(id => id !== studentId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.className || !formData.subjectName ||
        formData.selectedQuestions.length === 0 || formData.assignedStudents.length === 0) {
      alert('Please fill in all required fields and select at least one question and one student.');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.ASSIGNMENTS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'teacher-id': teacherId
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create assignment');
      }

      const result = await response.json();
      alert('Assignment created successfully!');
      onAssignmentCreated();
      onClose();
    } catch (error) {
      console.error('Error creating assignment:', error);
      alert('Failed to create assignment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && (!formData.title || !formData.className || !formData.subjectName)) {
      alert('Please fill in title, class, and subject.');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="modal-overlay">
      <div className="modal-content create-assignment-modal">
        <div className="modal-header">
          <h3>Create New Assignment</h3>
          <button className="close-btn" onClick={onClose}>Ã—</button>
        </div>

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div className="step-content">
              <h4>Basic Information</h4>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Class *</label>
                  <select
                    value={formData.className}
                    onChange={(e) => setFormData({...formData, className: e.target.value})}
                    required
                  >
                    <option value="">Select Class</option>
                    <option value="primary 1">Primary 1</option>
                    <option value="primary 2">Primary 2</option>
                    <option value="primary 3">Primary 3</option>
                    <option value="primary 4">Primary 4</option>
                    <option value="primary 5">Primary 5</option>
                    <option value="primary 6">Primary 6</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Subject *</label>
                  <select
                    value={formData.subjectName}
                    onChange={(e) => setFormData({...formData, subjectName: e.target.value})}
                    required
                  >
                    <option value="">Select Subject</option>
                    <option value="MATHS">Mathematics</option>
                    <option value="ENGLISH">English</option>
                    <option value="SCIENCE">Science</option>
                    <option value="HISTORY">History</option>
                    <option value="GEOGRAPHY">Geography</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="datetime-local"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Instructions</label>
                  <textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                    rows="2"
                    placeholder="Additional instructions for students..."
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              <h4>Select Questions from Exams</h4>
              <p>Select questions from midterm and final exams to include in this assignment:</p>

              <div className="questions-selection">
                {availableExams.length === 0 ? (
                  <div className="no-exams-message">
                    <p>No exams found. Please ensure exams exist for {formData.subjectName} in {formData.className}.</p>
                    <p className="text-sm text-gray-600 mt-2">
                      Contact an administrator to create exams first, or select a different subject/class combination.
                    </p>
                  </div>
                ) : (
                  (() => {
                    const filteredExams = availableExams.filter(exam =>
                      exam.subjectName === formData.subjectName && exam.className === formData.className
                    );

                    return filteredExams.length === 0 ? (
                      <div className="no-exams-message">
                        <p>No exams available for {formData.subjectName} in {formData.className}.</p>
                        <p className="text-sm text-gray-600 mt-2">
                          Please select a different subject or class, or contact an administrator to create exams.
                        </p>
                      </div>
                    ) : (
                      filteredExams.map(exam => (
                        <div key={exam.id} className="exam-section">
                          <h5>{exam.examType} - {exam.title || exam.subjectName}</h5>
                          <div className="questions-list">
                            {exam.questions?.map(question => (
                              <div key={question.questionId} className="question-item">
                                <label className="question-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={formData.selectedQuestions.includes(question.questionId)}
                                    onChange={(e) => handleQuestionSelect(question.questionId, e.target.checked)}
                                  />
                                  <div className="question-preview">
                                    <div className="question-text"
                                         dangerouslySetInnerHTML={{__html: question.questionText.substring(0, 100) + '...'}} />
                                    <div className="question-type">{question.questionType}</div>
                                  </div>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    );
                  })()
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="step-content">
              <h4>Assign to Students</h4>
              <p>Select students from {formData.className} to assign this work to:</p>

              <div className="students-selection">
                <div className="selection-controls">
                  <button
                    type="button"
                    className="select-all-btn"
                    onClick={() => setFormData({
                      ...formData,
                      assignedStudents: availableStudents.map(s => s.username)
                    })}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="select-none-btn"
                    onClick={() => setFormData({...formData, assignedStudents: []})}
                  >
                    Select None
                  </button>
                </div>

                <div className="students-list">
                  {availableStudents.map(student => (
                    <label key={student.username} className="student-item">
                      <input
                        type="checkbox"
                        checked={formData.assignedStudents.includes(student.username)}
                        onChange={(e) => handleStudentSelect(student.username, e.target.checked)}
                      />
                      <span>{student.firstName} {student.lastName} ({student.username})</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            {step > 1 && (
              <button type="button" className="prev-btn" onClick={prevStep}>
                Previous
              </button>
            )}
            {step < 3 ? (
              <button type="button" className="next-btn" onClick={nextStep}>
                Next
              </button>
            ) : (
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Creating...' : 'Create Assignment'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// Assignment Submissions Modal Component
const AssignmentSubmissionsModal = ({ assignment, teacherId, onClose }) => {
  const [submissions, setSubmissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmissions();
  }, [assignment.id]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/assignments/${assignment.id}/submissions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'teacher-id': teacherId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch submissions');
      }

      const data = await response.json();
      setSubmissions(data.submissions || []);
      setSummary(data.summary || null);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content submissions-modal">
        <div className="modal-header">
          <h3>Submissions for: {assignment.title}</h3>
          <button className="close-btn" onClick={onClose}>Ã—</button>
        </div>

        <div className="modal-body">
          {summary && (
            <div className="submission-summary">
              <div className="summary-stats">
                <div className="stat">
                  <span className="stat-number">{summary.totalAssigned}</span>
                  <span className="stat-label">Assigned</span>
                </div>
                <div className="stat">
                  <span className="stat-number">{summary.totalSubmitted}</span>
                  <span className="stat-label">Submitted</span>
                </div>
                <div className="stat">
                  <span className="stat-number">{summary.averageScore}%</span>
                  <span className="stat-label">Avg Score</span>
                </div>
              </div>
            </div>
          )}

          <div className="submissions-list">
            {loading ? (
              <div className="loading">Loading submissions...</div>
            ) : submissions.length === 0 ? (
              <div className="no-submissions">
                <p>No submissions yet.</p>
              </div>
            ) : (
              submissions.map(submission => (
                <div key={submission.id} className="submission-item">
                  <div className="submission-header">
                    <div className="student-info">
                      <strong>{submission.studentInfo?.firstName} {submission.studentInfo?.lastName}</strong>
                      <span className="student-username">({submission.studentId})</span>
                    </div>
                    <div className="submission-score">
                      <span className="score">{submission.score}/{submission.maxScore}</span>
                      <span className="percentage">({submission.percentage}%)</span>
                    </div>
                    <div className="submission-date">
                      {new Date(submission.submittedAt).toLocaleString()}
                    </div>
                  </div>

                  <div className="submission-details">
                    <div className="time-spent">Time spent: {Math.round(submission.timeSpent / 60)} minutes</div>
                    <button
                      className="view-details-btn"
                      onClick={() => {/* TODO: Show detailed submission view */}}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherAssignments;
